import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { getScheduleAvailabilities, ScheduleAvailabilityKind } from "client/Api/Calendar/calendar";
import { AppointmentRoomModel, getAppointmentRooms } from "client/Api/Email/Rooms/room";
import {
	FontIconName,
	HighlightedText,
	Icon,
	ListItem,
	MultiComboBox,
	MultiComboBoxSuggestionsResult,
	SuggestedMultiComboBoxItem,
	TagColor
} from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import { getHighlight } from "client/Misc/highlight";
import { localize } from "client_legacy/ndaw/culture";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	delay,
	EMPTY_ARRAY,
	ensureNotNil,
	isNil,
	isNilOrWhiteSpace,
	isNotNil
} from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import classNames from "classnames";
import styles from "./LocationInput.module.less";
import { TimeZone } from "client/DateAndTime/timezones";
import { formatForDto, isValidDate } from "client/DateAndTime/dateTimeUtils";
import { prepareEndTimeForSaving, prepareStartTimeForSaving } from "./ComposeAppointmentDialog/ComposeAppointmentDialog";
import { AppointmentLocationFormData } from "./common";

const MAX_NUMBER_OF_ITEMS = 10;

export type LocationInputProps = {
	attachable: IValueInputAttachable<readonly SuggestedMultiComboBoxItem<AppointmentLocationFormData>[]>;
	startTime: Date | null;
	endTime: Date | null;
	isAllDay: boolean;
	timeZone: TimeZone;
	shouldShowAvailability: boolean;
};

export const LocationInput = (props: LocationInputProps): ReactElement => {
	const {
		attachable,
		startTime,
		endTime,
		isAllDay,
		timeZone,
		shouldShowAvailability
	} = props;

	const [suggesionsResult, setSuggesionsResult] = useState<MultiComboBoxSuggestionsResult<AppointmentLocationFormData>>({
		loadingStatus: "loading"
	});

	const [locationAvailabilities, setLocationAvailabilities] = useState<Map<string, ScheduleAvailabilityKind>>(new Map());

	const findLocationsHandler = useCallback(
		async (searchTerm: string, signal: AbortSignal): Promise<void> => {
			if (searchTerm.trim() === "") {
				setSuggesionsResult({
					loadingStatus: "ready",
					suggestions: EMPTY_ARRAY
				});
				return;
			}

			setSuggesionsResult({
				loadingStatus: "loading"
			});

			try {
				await delay(400, signal);

				const locations: readonly AppointmentRoomModel[] = await getAppointmentRooms(
					searchTerm,
					MAX_NUMBER_OF_ITEMS,
					signal
				);

				setSuggesionsResult({
					loadingStatus: "ready",
					suggestions: locations.map(
						(x): SuggestedMultiComboBoxItem<AppointmentLocationFormData> => ({
							text: x.displayName,
							value: {
								id: x.id,
								displayName: x.displayName,
								availabilityKind: null
							},
							highlight: getHighlight(x.displayName, searchTerm),
							color: "secondary"
						})
					)
				});
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				setSuggesionsResult({ loadingStatus: "error" });
				console.error(e);
			}
		},
		[]
	);

	const locationIds = useMemo(
		(): readonly string[] => {
			if (suggesionsResult.loadingStatus !== "ready") {
				return [];
			}

			return suggesionsResult
				.suggestions
				.map(x => x.value.id)
				.filter(isNotNil);
		},
		[suggesionsResult]
	);

	useEffect(
		(): (() => void) | void => {
			const start: Date | null = prepareStartTimeForSaving(
				startTime,
				isAllDay,
				timeZone
			);

			const end: Date | null = prepareEndTimeForSaving(
				endTime,
				isAllDay,
				timeZone
			);

			if (isNil(start)
				|| !isValidDate(start)
				|| isNil(end)
				|| !isValidDate(endTime)
				|| locationIds.length === 0
				|| !shouldShowAvailability
			) {
				return;
			}

			async function fetch(signal: AbortSignal) {
				try {
					const result: Record<string, ScheduleAvailabilityKind> = await getScheduleAvailabilities(
						{
							startTime: ensureNotNil(formatForDto(start)),
							endTime: ensureNotNil(formatForDto(end)),
							smtpAddresses: locationIds
						},
						signal
					);

					const locationAvailabilities: Map<string, ScheduleAvailabilityKind> = new Map(
						Object.entries(result)
					);

					setLocationAvailabilities(locationAvailabilities);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e, "Error on getting location availabilities");
				}
			}

			const cancellation = new AbortController();
			fetch(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[
			startTime,
			endTime,
			isAllDay,
			timeZone,
			locationIds,
			shouldShowAvailability
		]
	);

	const getCustomSuggestion = useCallback(
		(searchTerm: string): SuggestedMultiComboBoxItem<AppointmentLocationFormData> | null => {
			return !isNilOrWhiteSpace(searchTerm)
				? {
					text: searchTerm,
					optionPrefix: localize("_UseThisLocation") + " ",
					value: { displayName: searchTerm },
					group: { name: "custom-suggestion" },
					color: "secondary"
				}
				: null;
		},
		[]
	);

	const suggesionsResultToRender = useMemo(
		(): MultiComboBoxSuggestionsResult<AppointmentLocationFormData> => {
			if (suggesionsResult.loadingStatus !== "ready") {
				return suggesionsResult;
			}

			if (locationAvailabilities.size === 0) {
				return suggesionsResult;
			}

			const { suggestions } = suggesionsResult;

			return {
				...suggesionsResult,
				suggestions: updateSuggestionsAvailabilityKind(
					suggestions,
					locationAvailabilities
				)
			};
		},
		[
			locationAvailabilities,
			suggesionsResult
		]
	);

	return (
		<MultiComboBox
			{...attachable.attachToMultiComboBox()}
			onFind={findLocationsHandler}
			placeholder={localize("_SearchForARoomOrLocation")}
			renderSuggestionBox={
				(props, ref) => (
					<MultiComboBox.SuggestionBox
						baseProps={props}
						ref={ref}
						suggestionsResult={suggesionsResultToRender}
						menuTitle={localize("_Location")}
						customSuggestion={getCustomSuggestion}
						emptySearchTermBehavior="show-hint"
					>
						{(item, onExecute): ReactElement => {
							const { value } = item;
							const availabilityKind: ScheduleAvailabilityKind | null = isNotNil(value?.id)
								? value.availabilityKind
								: null;

							return (
								<ListItem
									noMobileBorderBottom
									trackingId={isNotNil(value?.id) ? `location-${value.id}` : `location-by-text`}
								>
									<ListItem.Content
										onClick={onExecute}
									>
										<ListItem.Title
											trackingId="name"
										>
											{isNotNil(item.optionPrefix)
												? `${item.optionPrefix}${item.text}`
												: (
													<HighlightedText
														text={item.text}
														highlight={item.highlight}
													/>
												)
											}
										</ListItem.Title>

										{isNotNil(availabilityKind)
											&& availabilityKind !== ScheduleAvailabilityKind.Unknown
											&& shouldShowAvailability
											&& (
												<ListItem.SubTitle
													trackingId="availability"
												>
													<Status kind={availabilityKind} />
												</ListItem.SubTitle>
											)
										}
									</ListItem.Content>
								</ListItem>
							);
						}}
					</MultiComboBox.SuggestionBox>
				)
			}
		/>
	);
};
LocationInput.displayName="LocationInput";

type StatusKind = Exclude<ScheduleAvailabilityKind, ScheduleAvailabilityKind.Unknown>;

type StatusProps = {
	kind: StatusKind;
};

const Status = (props: StatusProps): ReactElement => {
	const {
		kind
	} = props;

	const {
		text,
		icon
	} = getStatusInfo(kind);

	return (
		<div
			className={classNames(
				styles.itemStatus,
				statusKindToStylesMap[kind]
			)}
		>
			<Icon value={icon} />
			{text}
		</div>
	);
};
Status.displayName = "Status";

type StatusInfo = {
	icon: FontIconName;
	text: string;
};

function getStatusInfo(
	kind: StatusKind
): StatusInfo {
	switch (kind) {
		case ScheduleAvailabilityKind.Available:
			return {
				icon: availabilityKindToFontIconNameMap[kind],
				text: localize("_Available")
			};
		case ScheduleAvailabilityKind.Unavailable:
			return {
				icon: availabilityKindToFontIconNameMap[kind],
				text: localize("_Unavailable")
			};
		default:
			never(kind);
	}
}

const statusKindToStylesMap: Record<StatusKind, string> = {
	[ScheduleAvailabilityKind.Available]: styles.itemStatus_available,
	[ScheduleAvailabilityKind.Unavailable]: styles.itemStatus_unavailable
};

function updateSuggestionsAvailabilityKind(
	suggestions: readonly SuggestedMultiComboBoxItem<AppointmentLocationFormData>[],
	locationAvailabilities: Map<string, ScheduleAvailabilityKind>
): readonly SuggestedMultiComboBoxItem<AppointmentLocationFormData>[] {
	return suggestions
		.map(
			x => {
				const { value } = x;

				if (isNil(value.id)) {
					return x;
				}

				const availabilityKind: ScheduleAvailabilityKind | undefined = locationAvailabilities
					.get(value.id);

				if (isNil(availabilityKind)) {
					return x;
				}

				return {
					...x,
					value: {
						...value,
						availabilityKind
					},
					icon: availabilityKind !== ScheduleAvailabilityKind.Unknown
						? availabilityKindToFontIconNameMap[availabilityKind]
						: undefined,
					color: availabilityKindToTagColorMap[availabilityKind]
				};
			}
		);
}

const availabilityKindToFontIconNameMap: Record<StatusKind, FontIconName> = {
	[ScheduleAvailabilityKind.Available]: "circle-check",
	[ScheduleAvailabilityKind.Unavailable]: "circle-xmark"
};

const availabilityKindToTagColorMap: Record<ScheduleAvailabilityKind, TagColor> = {
	[ScheduleAvailabilityKind.Available]: "success",
	[ScheduleAvailabilityKind.Unavailable]: "danger",
	[ScheduleAvailabilityKind.Unknown]: "secondary"
};